import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AllLeadsTab from './components/AllLeadsTab';
import InsightsTab from './components/InsightsTab';
import AdminPanel from './components/AdminPanel';
import LeadDetail from './components/LeadDetail';
import MeSheet from './components/MeSheet';
import NotificationCenter from './components/NotificationCenter';
import SocialInboxModal from './components/SocialInboxModal';
import QueueTab from './components/QueueTab';
  import UserManagementPanel from './components/UserManagementPanel';
  import { Softphone } from './components/Softphone';
import CallLogsTab from './components/CallLogsTab';
import AgentPresenceDebug from './components/AgentPresenceDebug';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import { useIsDesktop } from './hooks/useMediaQuery';
import { useCallQueueSSE } from './hooks/useCallQueueSSE';
import { useSocialInboxSSE } from './hooks/useSocialInboxSSE';
import { API_ENDPOINTS, hasSocialAccess } from './config';
import { useFollowupNotifications } from './hooks/useFollowupNotifications';
import { useWhatsAppListWebSocket, type WhatsAppListActivityEvent } from './hooks/useWhatsAppListWebSocket';
import { fetchAssignmentNotifications, fetchWaitingList, fetchLead, fetchMyRecentIvrCalls, fetchTelecallerProfile, fetchWhatsAppInbox, updateTelecallerCenter, clearIdentity } from './api';
import { usePolling } from './hooks/usePolling';
import { setAgentAvailable, subscribeCurrentAgentStatus } from './lib/agentPresence';
import { apiFetch } from './utils/api';
import type { Tab } from './types';
import type { SoftphoneHandle } from './components/Softphone';
import type { InAppNotification } from './hooks/useFollowupNotifications';
import { MessageCircleMore } from 'lucide-react';
import WhatsAppNeedsReply from './components/WhatsAppNeedsReply';
import InsightsWorkflowPrototype from './components/InsightsWorkflowPrototype';
import type { WhatsAppInboxItem } from './types';
import type { Telecaller } from './types';

const TAB_STORAGE_KEY = 'crm_active_tab';

const getSavedTab = (): Tab => {
  try {
    const saved = localStorage.getItem(TAB_STORAGE_KEY);
    const validTabs: Tab[] = ['unassigned', 'queue', 'leads', 'call-logs', 'insights'];
    if (saved && validTabs.includes(saved as Tab)) {
      return saved as Tab;
    }
    if (saved === 'all') return 'leads';
  } catch {
    // ignore
  }
  return 'queue';
};

const getTabs = (user: { role: string } | null) => {
  const canViewAllLeadsTab =
    user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_admin';

  return [
    { id: 'unassigned', label: 'New / Unassigned', icon: '🆕' },
    { id: 'queue', label: 'My Queue', icon: '⚡' },
    { id: 'leads', label: canViewAllLeadsTab ? 'All Leads' : 'My Leads', icon: '☰' },
    { id: 'call-logs', label: 'Call Logs', icon: '📞' },
  ] as { id: Tab; label: string; icon: string }[];
};

function AppContent() {
  const [tab, setTab] = useState<Tab>(getSavedTab());
  const [selected, setSelected] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [meOpen, setMeOpen] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [showUsersPanel, setShowUsersPanel] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showSocialInbox, setShowSocialInbox] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showWhatsAppInbox, setShowWhatsAppInbox] = useState(false);
  const [telecallerCenter, setTelecallerCenter] = useState<NonNullable<Telecaller['center']> | ''>('');
  const [centerPromptOpen, setCenterPromptOpen] = useState(false);
  const [centerSaving, setCenterSaving] = useState(false);
  const [assignmentNotification, setAssignmentNotification] = useState<{ lead_id: string; lead_name: string; center: string | null } | null>(null);
  const [completedIvrNotice, setCompletedIvrNotice] = useState<any>(null);
  const [recentIvrCalls, setRecentIvrCalls] = useState<any[]>([]);
  const [whatsappInbox, setWhatsAppInbox] = useState<WhatsAppInboxItem[]>([]);
  const [whatsappPendingCount, setWhatsAppPendingCount] = useState(0);
  const [lastWhatsAppActivity, setLastWhatsAppActivity] = useState<WhatsAppListActivityEvent | null>(null);
  const [followupNotifications, setFollowupNotifications] = useState<InAppNotification[]>([]);
  const seenIvrCallIdsRef = useRef<Set<string>>(new Set());
  const { user, isLoading, logout } = useAuth();
  const isDesktop = useIsDesktop();
  const softphoneRef = useRef<SoftphoneHandle | null>(null);
  const whatsappInboxRequestRef = useRef<Promise<void> | null>(null);
  const whatsappInboxRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const whatsappInboxRefreshQueuedRef = useRef(false);
  const whatsappInboxKeyRef = useRef('');
  const whatsappInboxRefreshRef = useRef<(immediate?: boolean) => void>(() => undefined);
  const ivrCursorRef = useRef<{ createdAt: string; id?: string } | undefined>(undefined);
  const assignmentCursorRef = useRef<{ createdAt: string; id?: string } | undefined>(undefined);

  const pollRecentIvr = useCallback(async () => {
    if (!user || user.role !== 'telecaller') return;
    const cursor = ivrCursorRef.current;
    const result = await fetchMyRecentIvrCalls(cursor);
    const { calls } = result;
    const incoming = calls || [];
    const key = (call: any) => String(call.id ?? call.call_uuid ?? `${call.created_at}:${call.lead_id ?? ''}`);
    const newCalls = incoming.filter((call) => !seenIvrCallIdsRef.current.has(key(call)));
    incoming.forEach((call) => seenIvrCallIdsRef.current.add(key(call)));
    if (!cursor) {
      setRecentIvrCalls(incoming);
    } else if (result.delta && incoming.length) {
      setRecentIvrCalls((current) => {
        const merged = [...incoming, ...current];
        const unique = new Map(merged.map((call) => [key(call), call]));
        return Array.from(unique.values()).sort((a, b) => String(b.finalized_at ?? b.created_at).localeCompare(String(a.finalized_at ?? a.created_at))).slice(0, 10);
      });
      const newest = newCalls[0];
      if (newest) setCompletedIvrNotice(newest);
    } else {
      setRecentIvrCalls(incoming);
      if (newCalls[0]) setCompletedIvrNotice(newCalls[0]);
    }
    const newest = incoming[0] ?? (!cursor ? incoming[0] : null);
    if (newest) ivrCursorRef.current = { createdAt: String(newest.finalized_at ?? newest.created_at), id: newest.id };
  }, [user]);

  useEffect(() => {
    ivrCursorRef.current = undefined;
    seenIvrCallIdsRef.current.clear();
    if (!user || user.role !== 'telecaller') {
      setCompletedIvrNotice(null);
      setRecentIvrCalls([]);
    }
  }, [user?.email, user?.role]);
  usePolling(pollRecentIvr, 15_000, [pollRecentIvr], {
    enabled: user?.role === 'telecaller',
    immediate: true,
    pauseWhenHidden: true,
  });

  useEffect(() => {
    if (!['telecaller', 'manager'].includes(user?.role ?? '')) {
      setCenterPromptOpen(false);
      return;
    }
    void fetchTelecallerProfile().then(({ me }) => {
      setTelecallerCenter(me.center ?? '');
      setCenterPromptOpen(!me.center);
    }).catch(() => undefined);
  }, [user?.email, user?.role]);


  const pollAssignmentNotifications = useCallback(async () => {
    if (!user || !['telecaller', 'manager'].includes(user.role)) return;
    const cursor = assignmentCursorRef.current ?? { createdAt: new Date(Date.now() - 30_000).toISOString() };
    const { notifications } = await fetchAssignmentNotifications(cursor);
    if (!notifications.length) return;
    const latest = notifications[notifications.length - 1];
    assignmentCursorRef.current = { createdAt: latest.created_at, id: latest.id };
    setAssignmentNotification(latest);
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification('Lead transferred — Santaan CRM', {
        body: latest.lead_name,
        tag: `assigned-${latest.lead_id}`,
      });
    }
  }, [user]);

  useEffect(() => {
    assignmentCursorRef.current = undefined;
  }, [user?.email, user?.role]);
  usePolling(pollAssignmentNotifications, 15_000, [pollAssignmentNotifications], {
    enabled: Boolean(user && ['telecaller', 'manager'].includes(user.role)),
    immediate: true,
    pauseWhenHidden: true,
  });

  async function saveTelecallerCenter() {
    if (!telecallerCenter) return;
    setCenterSaving(true);
    try {
      await updateTelecallerCenter(telecallerCenter);
      setCenterPromptOpen(false);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Unable to save center');
    } finally {
      setCenterSaving(false);
    }
  }
  
  const tabs = getTabs(user);
  const isPrivileged =
    user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canReceiveFollowupAlerts =
    user?.role === 'telecaller' || user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';
  const whatsappView = user?.role === 'telecaller' ? 'queue' : 'unassigned';
  const whatsappNotificationView = whatsappView;
  const whatsappInboxKey = user ? `${user.email}:${whatsappNotificationView}` : '';
  whatsappInboxKeyRef.current = whatsappInboxKey;
  const refreshWhatsAppInbox = useCallback((immediate = true) => {
    if (!user) return;
    if (immediate && document.visibilityState !== 'visible') return;

    if (!immediate) {
      if (whatsappInboxRefreshTimerRef.current !== null) return;
      whatsappInboxRefreshTimerRef.current = setTimeout(() => {
        whatsappInboxRefreshTimerRef.current = null;
        whatsappInboxRefreshRef.current(true);
      }, 150);
      return;
    }

    if (whatsappInboxRefreshTimerRef.current !== null) {
      clearTimeout(whatsappInboxRefreshTimerRef.current);
      whatsappInboxRefreshTimerRef.current = null;
    }
    if (whatsappInboxRequestRef.current) {
      whatsappInboxRefreshQueuedRef.current = true;
      return;
    }

    const requestKey = whatsappInboxKey;
    const request = fetchWhatsAppInbox(whatsappNotificationView).then((result) => {
      if (whatsappInboxKeyRef.current !== requestKey) return;
      setWhatsAppInbox(result.items);
      setWhatsAppPendingCount(result.pending_count);
    }).catch(() => undefined);
    whatsappInboxRequestRef.current = request;
    void request.finally(() => {
      if (whatsappInboxRequestRef.current === request) {
        whatsappInboxRequestRef.current = null;
      }
      if (whatsappInboxRefreshQueuedRef.current) {
        whatsappInboxRefreshQueuedRef.current = false;
        whatsappInboxRefreshRef.current(true);
      }
    }).catch(() => undefined);
  }, [user, whatsappInboxKey, whatsappNotificationView]);
  whatsappInboxRefreshRef.current = refreshWhatsAppInbox;

  useEffect(() => () => {
    if (whatsappInboxRefreshTimerRef.current !== null) {
      clearTimeout(whatsappInboxRefreshTimerRef.current);
      whatsappInboxRefreshTimerRef.current = null;
    }
    whatsappInboxRefreshQueuedRef.current = false;
  }, [user, whatsappNotificationView]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshWhatsAppInbox(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshWhatsAppInbox]);

  const handleWhatsAppRead = useCallback(() => {
    refreshWhatsAppInbox();
  }, [refreshWhatsAppInbox]);

  useWhatsAppListWebSocket(Boolean(user), whatsappView, (event) => {
    setLastWhatsAppActivity(event);
    refreshWhatsAppInbox(false);
  }, refreshWhatsAppInbox);

  useEffect(() => {
    refreshWhatsAppInbox();
  }, [refreshWhatsAppInbox]);
  
  // Enable follow-up notifications when user is logged in
  const {
    getNotifications: getFollowupNotifications,
    subscribe: subscribeFollowupNotifications,
    markAsRead: markFollowupAsRead,
    markAsReadForLead: markFollowupAsReadForLead,
    clearAll: clearFollowupNotifications,
  } = useFollowupNotifications(Boolean(user && canReceiveFollowupAlerts));

  const handleLeadSelect = useCallback((leadId: string) => {
    markFollowupAsReadForLead(leadId);
    setSelected(leadId);
  }, [markFollowupAsReadForLead]);

  // Save & next: open the first lead in "Waiting for you" other than the one just saved.
  const openNextWaiting = useCallback(async () => {
    try {
      const list = await fetchWaitingList();
      const next = list.buckets.flatMap((b) => b.items).find((item) => item.id !== selected);
      if (next) handleLeadSelect(next.id); else setSelected(null);
    } catch { setSelected(null); }
  }, [selected, handleLeadSelect]);

  // A helpline patient was just put through to this telecaller: open their lead so they know who it is.
  const patientCallSeenRef = useRef(0);
  const [patientCallNotice, setPatientCallNotice] = useState(false);
  useEffect(() => {
    if (!user?.email) return;
    return subscribeCurrentAgentStatus(user.email, (record) => {
      const at = Number((record as { incomingAt?: number } | null)?.incomingAt ?? 0);
      const leadId = (record as { incomingLeadId?: string | null } | null)?.incomingLeadId;
      if (!leadId || !at || at <= patientCallSeenRef.current) return;
      patientCallSeenRef.current = at;
      if (Date.now() - at > 120_000) return; // an old call seen on page load
      handleLeadSelect(leadId);
      setPatientCallNotice(true);
      window.setTimeout(() => setPatientCallNotice(false), 8000);
    });
  }, [user?.email, handleLeadSelect]);

  const handleNotificationLeadSelect = useCallback(async (leadId: string) => {
    try {
      await fetchLead(leadId);
      handleLeadSelect(leadId);
    } catch {
      // Stale or unauthorized notifications are dismissed without opening a lead.
    }
  }, [handleLeadSelect]);

  useEffect(() => {
    setFollowupNotifications(getFollowupNotifications());
    const unsubscribe = subscribeFollowupNotifications((updatedNotifications) => {
      setFollowupNotifications(updatedNotifications);
    });
    return unsubscribe;
  }, [getFollowupNotifications, subscribeFollowupNotifications]);

  // SSE connection for real-time call queue diagnostics
  const {
    waitingCalls,
    isConnected: sseConnected,
    connectionState: sseConnectionState,
    error: sseError,
    lastEvent: sseLastEvent,
  } = useCallQueueSSE(user?.email || '');

  const {
    pendingCount: socialPendingCount,
    lastEvent: socialSseKey,
  } = useSocialInboxSSE(Boolean(user));

  const handleHangupCall = async (callUuid: string) => {
    try {
      
      const response = await fetch(API_ENDPOINTS.CALL_QUEUE.HANGUP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_uuid: callUuid,
          agent_id: user?.email,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to hangup call');
      }

      if (user?.role === 'telecaller') {
        await setAgentAvailable({ email: user.email, name: user.email.split('@')[0] });
      }
      
      // Update agent status back to available
      await fetch(API_ENDPOINTS.AGENT.STATUS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user?.email,
          status: 'available',
        }),
      });

      alert('Call ended and converted to lead');
    } catch (error) {
      console.error('Failed to hangup call:', error);
      alert('Failed to hangup call: ' + String(error));
    }
  };

  // Persist tab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(TAB_STORAGE_KEY, tab);
    } catch {
      // ignore
    }
  }, [tab]);

  useEffect(() => {
    if (!isLoading && user && tab !== 'insights' && !tabs.find((t) => t.id === tab)) {
      setTab('queue');
    }
  }, [isLoading, tab, tabs, user]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-sm text-slate-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">CRM System</h1>
          <p className="text-slate-600 mb-6">Please sign in to access the dashboard</p>
          <div className="space-x-4">
            <a
              href="/login"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Sign In
            </a>
          </div>
        </div>
      </div>
    );
  }

  const userRole = user.role;

  const current = tabs.find((t) => t.id === tab);

  // Render the main column (header + tab content + bottom nav)
  const mainCol = (
    <div
      className={`flex flex-col bg-slate-50 ${
        isDesktop ? 'h-screen w-[28rem] flex-none border-r border-slate-200' : 'min-h-screen mx-auto max-w-md shadow-xl'
      }`}
    >
      <header className={`sticky top-0 z-10 bg-wechat-header text-white ${isDesktop ? 'flex h-11 flex-none items-center px-3' : 'px-4 pb-3 pt-[env(safe-area-inset-top,12px)]'}`}>
        <div className="flex w-full items-center justify-between">
          <div>
            <div className={`uppercase tracking-widest text-slate-300 ${isDesktop ? 'text-[11px] font-semibold' : 'text-xs'}`}>Edge CRM</div>
            {!isDesktop && <h1 className="text-lg font-semibold">{current?.label}</h1>}
          </div>
          <div className="flex items-center gap-2">
            {canReceiveFollowupAlerts && (
              <NotificationCenter
                onNotificationClick={handleNotificationLeadSelect}
                notifications={followupNotifications}
                markAsRead={markFollowupAsRead}
                clearAll={clearFollowupNotifications}
              />
            )}
            {hasSocialAccess(user.role, user.email) && (
              <button
                onClick={() => setShowSocialInbox(true)}
                className="relative flex items-center gap-2 rounded-full bg-slate-700/60 px-3 py-1 text-xs text-slate-100 active:bg-slate-700"
              >
                <MessageCircleMore className="h-4 w-4" />
                Social
                {socialPendingCount > 0 && (
                  <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                    {socialPendingCount > 9 ? '9+' : socialPendingCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setShowWhatsAppInbox((open) => !open)}
              className="relative flex items-center gap-2 rounded-full bg-emerald-700/80 px-3 py-1 text-xs text-white active:bg-emerald-700"
              aria-label="WhatsApp needs reply"
            >
              WhatsApp
              {whatsappPendingCount > 0 && (
                <span className="min-w-5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {whatsappPendingCount > 99 ? '99+' : whatsappPendingCount}
                </span>
              )}
            </button>
            <div className="relative">
              <button
                onClick={() => setMenuOpen((open) => !open)}
                className="flex items-center gap-2 rounded-full bg-slate-700/60 px-3 py-1 text-xs text-slate-100 active:bg-slate-700"
              >
                <span className="h-2 w-2 rounded-full bg-wechat-accent" />
                <span className="truncate max-w-[120px]">{user.email.split('@')[0] || 'Me'}</span>
                <svg className={`h-3 w-3 transition-transform ${menuOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 4.5L6 7.5L9 4.5" />
                </svg>
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-full z-40 mt-1 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
                    {isAdmin && (
                      <button
                        onClick={() => { setShowUsersPanel(true); setMenuOpen(false); }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        Users
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => { setShowAdminPanel(true); setMenuOpen(false); }}
                        className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                        </svg>
                        Admin Panel
                      </button>
                    )}
                    <div className="my-1 border-t border-slate-100" />
                    <button
                      onClick={() => { setTab('insights'); setMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" />
                      </svg>
                      Insights
                    </button>
                    <button
                      onClick={() => { setMeOpen(true); setMenuOpen(false); }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                      </svg>
                      My Profile
                    </button>
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        if (confirm('Sign out and clear local session?')) {
                          clearIdentity();
                          void logout();
                        }
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-xs font-medium text-rose-600 hover:bg-rose-50"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {showWhatsAppInbox && (
        <WhatsAppNeedsReply
          items={whatsappInbox}
          pendingCount={whatsappPendingCount}
          onSelect={(leadId) => {
            setShowWhatsAppInbox(false);
            handleLeadSelect(leadId);
          }}
        />
      )}

      <main className={`min-h-0 flex-1 ${isDesktop ? 'overflow-y-auto' : 'pb-60'}`}>
        <section hidden={tab !== 'unassigned'}>
          <QueueTab
            active={tab === 'unassigned'}
            onSelect={handleLeadSelect}
            onChanged={() => setReloadKey((n) => n + 1)}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            view="unassigned"
            title="New / Unassigned Leads"
            subtitle="Claim a lead before you start working it."
            desktop={isDesktop}
            queueSseConnectionState={sseConnectionState}
            queueSseSignalKey={sseLastEvent}
            whatsappActivityEvent={lastWhatsAppActivity}
            canRestoreArchived={isPrivileged}
          />
        </section>
        <section hidden={tab !== 'queue'} className={isDesktop ? 'h-full' : undefined}>
          <QueueTab
            active={tab === 'queue'}
            onSelect={handleLeadSelect}
            onChanged={() => setReloadKey((n) => n + 1)}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            view="queue"
            title="My Queue"
            subtitle="Active leads that need attention now."
            desktop={isDesktop}
            queueSseConnectionState={sseConnectionState}
            queueSseSignalKey={sseLastEvent}
            whatsappActivityEvent={lastWhatsAppActivity}
            canRestoreArchived={isPrivileged}
          />
        </section>
        <section hidden={tab !== 'leads'}>
          <AllLeadsTab
            active={tab === 'leads'}
            onSelect={handleLeadSelect}
            reloadKey={reloadKey}
            me={user.email}
            selectedId={selected}
            role={user.role}
          />
        </section>
        <section hidden={tab !== 'call-logs'}>
          <CallLogsTab
            active={tab === 'call-logs'}
            onSelect={handleLeadSelect}
            privileged={isPrivileged}
            recentIvrCalls={recentIvrCalls}
          />
        </section>
        <section hidden={tab !== 'insights'}>
          <InsightsTab active={tab === 'insights'} reloadKey={reloadKey} role={user.role} onOpenLead={handleLeadSelect} />
        </section>
      </main>

      <nav
        className={`fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur ${
          isDesktop ? 'left-0 right-auto w-[28rem] max-w-md' : 'mx-auto max-w-md'
        }`}
      >
        <div className="flex justify-center items-center gap-4 px-4">
          {tabs.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex flex-col items-center gap-0.5 py-2 px-3 text-[11px] ${
                  active ? 'text-wechat-accent font-semibold' : 'text-slate-500'
                }`}
              >
                <span className="text-lg leading-none">{t.icon}</span>
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {showUsersPanel && isAdmin && (
        <div className="fixed inset-0 z-50 bg-slate-900/40">
          <div className="absolute right-0 top-0 h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Admin</div>
                <h2 className="text-base font-semibold text-slate-900">Users</h2>
              </div>
              <button onClick={() => setShowUsersPanel(false)} className="text-sm text-slate-500">
                Close
              </button>
            </div>
            <UserManagementPanel active={showUsersPanel} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-full bg-wechat-bg">
      {isDesktop ? (
        // Desktop split-pane: list on left, detail on right
        <div className="mx-auto flex h-screen max-w-[90rem] bg-slate-50 shadow-xl overflow-hidden">
          {mainCol}
          <div className="flex flex-1 flex-col overflow-hidden">
            {selected ? (
              <LeadDetail
                leadId={selected}
                me={user.email}
                userRole={userRole}
                onClose={() => setSelected(null)}
                onChanged={() => setReloadKey((n) => n + 1)}
                onWhatsAppRead={handleWhatsAppRead}
                onNext={() => void openNextWaiting()}
                inline
              />
            ) : (
              <div className="flex flex-1 items-center justify-center bg-slate-50 p-12 text-center overflow-auto">
                <div className="max-w-sm">
                  <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-200 text-2xl">
                    ⚡
                  </div>
                  <h2 className="text-base font-semibold text-slate-700">Pick a lead</h2>
                  <p className="mt-1 text-sm text-slate-500">
                    Tap any card on the left to see the full timeline, call history, WhatsApp
                    templates, and quick-action buttons.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {mainCol}
          {selected && (
            <LeadDetail
              leadId={selected}
              me={user.email}
              userRole={userRole}
              onClose={() => setSelected(null)}
              onChanged={() => setReloadKey((n) => n + 1)}
              onWhatsAppRead={handleWhatsAppRead}
              onNext={() => void openNextWaiting()}
            />
          )}
        </>
      )}

      {showAdminPanel && isAdmin && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}

      {meOpen && (
        <MeSheet
          onClose={() => setMeOpen(false)}
          onSignOut={async () => {
            await logout();
            setMeOpen(false);
          }}
        />
      )}

      {showSocialInbox && (
        <SocialInboxModal
          isOpen={showSocialInbox}
          onClose={() => setShowSocialInbox(false)}
          onOpenLead={handleLeadSelect}
          socialSignalKey={socialSseKey}
          viewerEmail={user.email}
          seeAll={['admin', 'manager', 'super_admin'].includes(user.role) || ['swainr951@gmail.com', 'sahoodipti969@gamil.com'].includes(user.email.trim().toLowerCase())}
        />
      )}

      {patientCallNotice && (
        <div role="status" className="fixed left-1/2 top-4 z-[70] -translate-x-1/2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-lg">
          Patient call from the helpline — their lead is open
        </div>
      )}
      {completedIvrNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600">IVR call completed</div>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">Patient you just spoke with</h2>
            <p className="mt-3 text-2xl font-bold tracking-wide text-slate-900">{completedIvrNotice.customer_phone || completedIvrNotice.lead_phone || 'Unknown number'}</p>
            <p className="mt-1 text-sm text-slate-500">{completedIvrNotice.patient_name || 'Unknown Patient'}</p>
            <div className="mt-5 flex gap-2">
              {completedIvrNotice.lead_id && <button type="button" onClick={() => { setCompletedIvrNotice(null); handleLeadSelect(completedIvrNotice.lead_id); }} className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">Open lead</button>}
              <button type="button" onClick={() => setCompletedIvrNotice(null)} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Dismiss</button>
            </div>
          </div>
        </div>
      )}


      {/* Debug Panel */}
      {showDebugPanel && (
        <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg p-4 w-80 border border-gray-200">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-sm">SSE Debug Panel</h3>
            <button onClick={() => setShowDebugPanel(false)} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">SSE Connected:</span>
              <span className={sseConnected ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                {sseConnected ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Waiting Calls:</span>
              <span className="font-medium">{waitingCalls.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Last Event:</span>
              <span className="font-medium">{sseLastEvent ? new Date(sseLastEvent).toLocaleTimeString() : 'N/A'}</span>
            </div>
            {sseError && (
              <div className="flex justify-between">
                <span className="text-gray-600">Error:</span>
                <span className="text-red-600">{sseError}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">User:</span>
              <span className="font-medium">{user?.email || 'N/A'}</span>
            </div>
            {(isPrivileged || import.meta.env.DEV) && <AgentPresenceDebug />}
            <div className="pt-2 border-t">
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.TEST.QUEUE_EVENT, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ customer_phone: '919999999999', campaign: 'test' }),
                    });
                    const data = await response.json();
                    alert('Test queue event created: ' + data.queue_id);
                  } catch (error) {
                    console.error('Failed to create test queue event:', error);
                    alert('Failed to create test queue event');
                  }
                }}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded text-xs mb-2"
              >
                Test Queue Event
              </button>
              <button
                onClick={async () => {
                  try {
                    const response = await fetch(API_ENDPOINTS.CALL_QUEUE.HANGUP, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        call_uuid: 'test-uuid',
                        agent_id: user?.email,
                      }),
                    });
                    const data = await response.json();
                    alert('Call hung up successfully');
                  } catch (error) {
                    console.error('Failed to hangup call:', error);
                    alert('Failed to hangup call');
                  }
                }}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded text-xs"
              >
                Test Hangup (Manual)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Toggle Button */}
      <button
        onClick={() => setShowDebugPanel(!showDebugPanel)}
        className="fixed bottom-20 right-4 z-40 bg-gray-800 text-white p-2 rounded-full shadow-lg hover:bg-gray-700"
        title="Toggle Debug Panel"
      >
        🐛
      </button>

      {user.role === 'telecaller' && (
        <Softphone
          ref={softphoneRef}
          agentId={user.email}
          presenceEnabled={true}
        />
      )}

      {centerPromptOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
            <h2 className="text-lg font-semibold text-slate-900">Select your center</h2>
            <p className="mt-1 text-sm text-slate-600">Choose the center you work for before receiving qualified leads.</p>
            <select value={telecallerCenter} onChange={(event) => setTelecallerCenter(event.target.value as NonNullable<Telecaller['center']> | '')} disabled={centerSaving} className="mt-4 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm">
              <option value="">Select center</option>
              <option value="BBSR">BBSR</option>
              <option value="BAM">BAM</option>
              <option value="Angule">Angule</option>
            </select>
            <button onClick={() => void saveTelecallerCenter()} disabled={!telecallerCenter || centerSaving} className="mt-4 w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{centerSaving ? 'Saving…' : 'Save center'}</button>
          </div>
        </div>
      )}

      {assignmentNotification && (
        <div className="fixed right-4 top-16 z-[60] w-80 rounded-xl border border-emerald-200 bg-white p-4 shadow-2xl">
          <div className="font-semibold text-emerald-900">Lead transferred to you</div>
          <div className="mt-1 text-sm text-slate-700">{assignmentNotification.lead_name}</div>
          <button onClick={() => { handleLeadSelect(assignmentNotification.lead_id); setAssignmentNotification(null); }} className="mt-3 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white">Open lead</button>
          <button onClick={() => setAssignmentNotification(null)} className="ml-2 text-xs text-slate-600">Dismiss</button>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {import.meta.env.DEV && (
            <Route path="/prototype/insights" element={<div className="min-h-screen bg-slate-50"><InsightsWorkflowPrototype /></div>} />
          )}
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}
